import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import MangaCard from '@/components/feature/MangaCard';
import { hotMangaList, allMangaList, categories } from '@/mocks/manga';

export default function HomePage() {
  const { t } = useTranslation();

  const completedManga = allMangaList.filter((m) => m.status === 'completed').slice(0, 8);
  const latestManga = [...allMangaList].sort((a, b) =>
    new Date(b.updateTime).getTime() - new Date(a.updateTime).getTime()
  ).slice(0, 8);

  const categoryDisplay = categories.filter((c) => c.slug !== 'all');

  return (
    <>
      {/* SEO Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebPage",
          "name": "咻咻漫画 - 全集漫画免费阅读平台",
          "description": "咻咻漫画为您汇集全集漫画资源，包含全集韩漫、全集国漫及日漫完结佳作，全部免费下拉式畅读，告别追更等待。",
          "url": "https://htb88.cn/",
          "inLanguage": "zh-CN",
          "isPartOf": {
            "@type": "WebSite",
            "name": "咻咻漫画",
            "url": "https://htb88.cn/"
          }
        })}
      </script>

      {/* Hero Section */}
      <section className="relative w-full h-[420px] md:h-[600px] overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=A%20stylized%20artistic%20manga%20comic%20themed%20abstract%20background%20with%20warm%20orange%20amber%20and%20gold%20gradient%2C%20subtle%20ink%20splash%20textures%2C%20dynamic%20motion%20lines%2C%20manga%20panel%20abstract%20patterns%2C%20warm%20atmospheric%20lighting%2C%20clean%20modern%20composition%2C%20no%20text%2C%20digital%20art%20illustration%20style&width=1600&height=900&seq=hero-bg-manga&orientation=landscape"
          alt="咻咻漫画 - 全集漫画免费阅读"
          title="咻咻漫画"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/50" />
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4">
          <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl text-white mb-4 drop-shadow-lg">
            {t('hero.title')}
          </h1>
          <p className="text-lg md:text-xl text-white/90 mb-2 font-medium">{t('hero.subtitle')}</p>
          <p className="text-sm md:text-base text-white/70 max-w-xl mb-8 leading-relaxed">{t('hero.description')}</p>
          <div className="flex flex-col sm:flex-row items-center gap-3">
            <Link
              to="/category/all"
              className="px-8 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 rounded-full text-sm font-semibold transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer"
            >
              {t('hero.explore')}
            </Link>
            <Link
              to="/category/completed"
              className="px-8 py-3 bg-white/15 hover:bg-white/25 text-white rounded-full text-sm font-semibold backdrop-blur-sm transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              {t('nav.completed')}
            </Link>
          </div>
        </div>
      </section>

      {/* Hot Manga Section */}
      <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-50">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-end justify-between mb-6">
            <div>
              <h2 className="text-xl md:text-2xl font-heading text-foreground-900">{t('section.hot')}</h2>
              <p className="text-sm text-foreground-500 mt-1">{t('section.hot.desc')}</p>
            </div>
            <Link to="/category/all?sort=popular" className="text-sm text-primary-500 hover:text-primary-600 font-medium whitespace-nowrap cursor-pointer">
              查看全部 <i className="ri-arrow-right-line ml-1"></i>
            </Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5" data-product-shop>
            {hotMangaList.slice(0, 5).map((manga) => (
              <MangaCard key={manga.id} manga={manga} />
            ))}
          </div>
        </div>
      </section>

      {/* Category Navigation Section */}
      <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8">
            <h2 className="text-xl md:text-2xl font-heading text-foreground-900">{t('section.category')}</h2>
            <p className="text-sm text-foreground-500 mt-1">{t('section.category.desc')}</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {categoryDisplay.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className="group relative overflow-hidden rounded-lg aspect-[16/10] cursor-pointer"
              >
                <div className={`absolute inset-0 ${cat.color} opacity-90 transition-opacity group-hover:opacity-100`} />
                <div className="absolute inset-0 flex flex-col items-center justify-center text-white p-4">
                  <div className="w-12 h-12 flex items-center justify-center mb-3">
                    <i className={`${cat.icon} text-3xl`}></i>
                  </div>
                  <h3 className="font-heading text-lg md:text-xl whitespace-nowrap">{cat.name}</h3>
                  <p className="text-xs text-white/70 mt-1">立即浏览</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Completed Manga Section */}
      <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-50">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-end justify-between mb-6">
            <div>
              <h2 className="text-xl md:text-2xl font-heading text-foreground-900">{t('section.completed')}</h2>
              <p className="text-sm text-foreground-500 mt-1">{t('section.completed.desc')}</p>
            </div>
            <Link to="/category/completed" className="text-sm text-primary-500 hover:text-primary-600 font-medium whitespace-nowrap cursor-pointer">
              查看全部 <i className="ri-arrow-right-line ml-1"></i>
            </Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 md:gap-5" data-product-shop>
            {completedManga.map((manga) => (
              <MangaCard key={manga.id} manga={manga} />
            ))}
          </div>
        </div>
      </section>

      {/* Latest Updates Section */}
      <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-end justify-between mb-6">
            <div>
              <h2 className="text-xl md:text-2xl font-heading text-foreground-900">{t('section.latest')}</h2>
              <p className="text-sm text-foreground-500 mt-1">{t('section.latest.desc')}</p>
            </div>
            <Link to="/category/all?sort=latest" className="text-sm text-primary-500 hover:text-primary-600 font-medium whitespace-nowrap cursor-pointer">
              查看全部 <i className="ri-arrow-right-line ml-1"></i>
            </Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 md:gap-5" data-product-shop>
            {latestManga.map((manga) => (
              <MangaCard key={manga.id} manga={manga} />
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-50">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-xl md:text-2xl font-heading text-foreground-900 text-center mb-8">常见问题</h2>
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              "mainEntity": [
                { "@type": "Question", "name": "咻咻漫画上的所有漫画都是免费的吗？", "acceptedAnswer": { "@type": "Answer", "text": "是的，咻咻漫画平台上的所有漫画资源完全免费，无需任何付费订阅即可畅读全部内容。" } },
                { "@type": "Question", "name": "网站上的漫画都是全集完结的吗？", "acceptedAnswer": { "@type": "Answer", "text": "咻咻漫画专注于收录已完结的漫画作品，包括韩漫全集、国漫全集和日漫完结佳作。" } },
                { "@type": "Question", "name": "什么是下拉式阅读模式？", "acceptedAnswer": { "@type": "Answer", "text": "下拉式阅读让您可以像浏览网页一样，通过向下滚动来连续阅读漫画内容，体验更加流畅自然。" } },
                { "@type": "Question", "name": "支持在手机上阅读吗？", "acceptedAnswer": { "@type": "Answer", "text": "当然支持！咻咻漫画网站采用响应式设计，完美适配手机、平板和电脑等各种设备。" } },
                { "@type": "Question", "name": "漫画资源会定期更新吗？", "acceptedAnswer": { "@type": "Answer", "text": "会的！我们的编辑团队会持续收录最新完结的优质漫画作品，不断丰富平台资源库。" } }
              ]
            })}
          </script>
          <div className="space-y-4">
            {[
              { q: "咻咻漫画上的所有漫画都是免费的吗？", a: "是的，咻咻漫画平台上的所有漫画资源完全免费，无需任何付费订阅即可畅读全部内容。我们致力于为广大漫画爱好者提供零门槛的阅读体验。" },
              { q: "网站上的漫画都是全集完结的吗？", a: "咻咻漫画专注于收录已完结的漫画作品，包括韩漫全集、国漫全集和日漫完结佳作。我们希望读者能够一次性完整地阅读喜欢的作品，而不是在追更中等待。" },
              { q: "什么是下拉式阅读模式？", a: "下拉式阅读是咻咻漫画的特色阅读方式。与传统的翻页阅读不同，下拉式阅读让您可以像浏览网页一样，通过向下滚动来连续阅读漫画内容，体验更加流畅自然。" },
              { q: "支持在手机上阅读吗？", a: "当然支持！咻咻漫画网站采用响应式设计，完美适配手机、平板和电脑等各种设备。无论您使用什么设备，都能获得舒适的下拉式阅读体验。" },
              { q: "漫画资源会定期更新吗？", a: "会的！我们的编辑团队会持续收录最新完结的优质漫画作品，不断丰富平台资源库。建议您定期访问或关注我们的社交媒体账号获取最新上架信息。" },
            ].map((faq, idx) => (
              <details key={idx} className="group bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
                <summary className="flex items-center justify-between px-5 py-4 cursor-pointer text-sm font-medium text-foreground-800 hover:text-primary-600 transition-colors">
                  {faq.q}
                  <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform"></i>
                </summary>
                <div className="px-5 pb-4 text-sm text-foreground-600 leading-relaxed">
                  {faq.a}
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}